import React, { useState, useEffect, useCallback } from 'react';
// Data
import { galleryImagesArray } from '../../utils/data/GalleryData';

function MainGalleryComponent() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const openViewer = (index) => {
    setActiveIndex(index);
    setIsOpen(true);
  };

  const closeViewer = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(null);
  }, []);

  // Close on ESC + lock body scroll while open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeViewer();
      if (isOpen && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        setActiveIndex((prev) => {
          if (prev == null) return prev;
          const last = galleryImagesArray.length - 1;
          if (e.key === 'ArrowRight') return prev === last ? 0 : prev + 1;
          if (e.key === 'ArrowLeft') return prev === 0 ? last : prev - 1;
          return prev;
        });
      }
    };
    document.addEventListener('keydown', onKey);
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
        document.removeEventListener('keydown', onKey);
      };
    }
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeViewer]);

  return (
    <div className='grid container mx-auto w-full h-full justify-center items-center py-2 px-8 md:px-12 lg:px-20 xl:px-28'>
      <section className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-8 lg:gap-y-16 lg:gap-x-12 gap-x-4'>
        {galleryImagesArray.map((image, index) => (
          <div
            key={index}
            className='flex gap-2 lg:gap-4 flex-col items-center'
          >
            {/* Title Section */}
            <div className='grid grid-cols-reg h-fit gap-6 w-full overflow-hidden items-center'>
              <div>
                <p className='font-titleFont text-sm md:text-xs lg:text-lg tracking-widest'>
                  {image.name}
                </p>
              </div>
              <div className='grid items-center'>
                <div
                  className='h-[2px] bg-colour3'
                  role='separator'
                  aria-hidden='true'
                ></div>
              </div>
            </div>

            {/* Image with Zoom Effect on Hover */}
            <div className='w-full h-full max-h-[80vh] group shado'>
              <div className='grid h-full bg-colour4 p-1 rounded-xl overflow-hidden'>
                <div className='grid h-full bg-colour6 p-1 rounded-lg'>
                  <div className='grid h-full bg-colour4 p-1.5 rounded-lg overflow-hidden'>
                    <img
                      src={image.src}
                      alt={image.alt}
                      className='w-full h-full object-cover transition-transform duration-[3s] rounded-md group-hover:scale-125'
                      loading='lazy'
                      onClick={() => openViewer(index)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Fullscreen Viewer */}
      {isOpen && activeIndex != null && (
        <div
          className='fixed inset-0 z-[9999] grid bg-black/90'
          role='dialog'
          aria-modal='true'
          aria-label='Image viewer'
          onClick={closeViewer} // backdrop click closes
        >
          {/* Content grid to avoid absolute positioning */}
          <div
            className='grid grid-rows-[auto,1fr] grid-cols-1 w-full h-full'
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking content
          >
            {/* Top bar with close */}
            <div className='grid grid-cols-[1fr_auto] items-center p-4'>
              <div className='grid'>
                <p className='text-colour2 text-sm md:text-base lg:text-lg'>
                  {galleryImagesArray[activeIndex].name}
                </p>
              </div>
              <div className='grid'>
                <button
                  type='button'
                  className='grid px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm'
                  onClick={closeViewer}
                  aria-label='Close full screen viewer'
                >
                  Close
                </button>
              </div>
            </div>

            {/* Image area */}
            <div className='grid place-items-center px-4 pb-6'>
              <img
                src={galleryImagesArray[activeIndex].src}
                alt={galleryImagesArray[activeIndex].alt}
                className='max-h-[85vh] max-w-[95vw] object-contain rounded-lg'
                draggable='false'
              />
            </div>

            {/* Optional: simple prev/next controls (grid-only) */}
            <div className='grid grid-cols-2 gap-4 px-4 pb-6'>
              <button
                type='button'
                className='grid justify-center px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm'
                onClick={() =>
                  setActiveIndex((prev) =>
                    prev === 0 ? galleryImagesArray.length - 1 : prev - 1
                  )
                }
                aria-label='Previous image'
              >
                ‹ Prev
              </button>
              <button
                type='button'
                className='grid justify-center px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm'
                onClick={() =>
                  setActiveIndex((prev) =>
                    prev === galleryImagesArray.length - 1 ? 0 : prev + 1
                  )
                }
                aria-label='Next image'
              >
                Next ›
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MainGalleryComponent;
